import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Box, Checkbox, Flex, spaces, Text } from '@electricjs/arc';

import { Employee } from '@/types/employees';

import { EmployeeWithReminderReason } from '@/pages/Hardware/DeviceOverview/types';
import { getDisplayFullName } from 'common/utils/getFullName';

const StyledSelectAll = styled(Flex)`
  display: grid;
  grid-template-columns: 3.5rem 1fr;
  align-items: center;
`;

const StyledFlex = styled(Flex)`
  display: grid;
  grid-template-columns: 3.5rem minmax(10px, 1.5fr) minmax(10px, 1.5fr) minmax(
      10px,
      1.5fr
    );
  align-items: top;
`;

function isEmployeeWithReminderReason(
  obj: Employee
): obj is EmployeeWithReminderReason {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'reminderReason' in obj &&
    typeof obj.reminderReason === 'string'
  );
}

type EmployeeCheckboxProps<GenericEmployee extends Employee> = {
  employee: GenericEmployee;
  onChange: (currEmployee: GenericEmployee, isSelected: boolean) => void;
  isSelected: boolean;
  includeEmail?: boolean;
  includeDevice?: boolean;
};

const EmployeeCheckbox = <GenericEmployee extends Employee>({
  employee,
  onChange,
  isSelected,
  includeEmail = false,
  includeDevice = true,
}: EmployeeCheckboxProps<GenericEmployee>) => {
  const handleChange = () => {
    onChange(employee, isSelected);
  };

  return (
    <StyledFlex key={employee.id} mt={spaces.space_xs}>
      <Checkbox
        id={`${employee.id}-employee-checkbox`}
        onChange={handleChange}
        checked={isSelected}
      />

      {isEmployeeWithReminderReason(employee) && includeDevice && (
        <Flex vAlignContent={'center'}>
          <Text mt={spaces.space_xs} mr={spaces.space_2xs}>
            {employee.asset?.name ||
              employee.asset?.model ||
              employee.asset?.device?.hardwareModel ||
              '-- --'}
          </Text>
        </Flex>
      )}
      <Text mt={spaces.space_xs}>{getDisplayFullName(employee)}</Text>
      {includeEmail && <Text mt={spaces.space_xs}>{employee.email}</Text>}
    </StyledFlex>
  );
};

type EmployeeCheckboxListProps<GenericEmployee extends Employee> = {
  employees: GenericEmployee[];
  selectedEmployees: GenericEmployee[];
  setSelectedEmployees: React.Dispatch<React.SetStateAction<GenericEmployee[]>>;
  includeDevice?: boolean;
  includeEmail?: boolean;
};

const EmployeeCheckboxList = <GenericEmployee extends Employee>({
  employees,
  selectedEmployees,
  setSelectedEmployees,
  includeEmail = false,
  includeDevice = true,
}: EmployeeCheckboxListProps<GenericEmployee>) => {
  const { t } = useTranslation('assets');
  const allEmployeesSelected = employees.length === selectedEmployees.length;

  const selectedEmployeeMap: { [key: string]: boolean } =
    selectedEmployees.reduce((acc, currEmployee) => {
      return { ...acc, [currEmployee.id]: true };
    }, {});

  const handleChange = (currEmployee: GenericEmployee, isSelected: boolean) => {
    isSelected
      ? setSelectedEmployees(
          selectedEmployees.filter(employee => employee.id !== currEmployee.id)
        )
      : setSelectedEmployees([...selectedEmployees, currEmployee]);
  };
  const handleSelectAll = () => {
    allEmployeesSelected
      ? setSelectedEmployees([])
      : setSelectedEmployees(employees);
  };

  const selectionNoun = includeDevice
    ? t('appInviteModal.device', { count: employees.length })
    : t('appInviteModal.employee', { count: employees.length });

  return (
    <>
      <StyledSelectAll mt={spaces.space_s}>
        <Checkbox
          id="select-all-checkbox"
          checked={allEmployeesSelected}
          onClick={handleSelectAll}
        />
        <Text variant="label-large">
          {t('appInviteModal.select')}{' '}
          {employees.length > 1 ? t('appInviteModal.all') : ''}{' '}
          {employees.length} {selectionNoun}
        </Text>
      </StyledSelectAll>
      <StyledFlex mt={spaces.space_m}>
        <Box />
        {includeDevice && (
          <Text variant="label-large">{t('appInviteModal.deviceColumn')}</Text>
        )}
        <Text variant="label-large">{t('appInviteModal.deviceOwner')}</Text>
        {includeEmail && (
          <Text variant="label-large">{t('appInviteModal.email')}</Text>
        )}
      </StyledFlex>
      {employees.map(employee => (
        <EmployeeCheckbox
          includeDevice={includeDevice}
          key={
            isEmployeeWithReminderReason(employee)
              ? employee.id + employee?.serialIfMultipleDevices
              : employee.id
          }
          employee={employee}
          onChange={handleChange}
          isSelected={!!selectedEmployeeMap[employee.id]}
          includeEmail={includeEmail}
        />
      ))}
    </>
  );
};

export default EmployeeCheckboxList;
